import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { 
  Employee, 
  ApiResponse, 
  EmployeeListResponse,
  GenderLookup,
  DepartmentLookup,
  CountryLookup,
  CityLookup,
  ProfessionIqamaLookup,
  NoticePeriodLookup,
  MaritalStatusLookup,
  JobLookup
} from '@/api/types'

/**
 * Employee management service
 * Handles all employee-related API operations with proper typing
 */
export class EmployeesService {
  /**
   * Get paginated list of employees
   */
  async getAllEmployees(
    branchId: number, 
    pageNumber: number, 
    pageSize: number
  ): Promise<EmployeeListResponse> {
    const url = endpoints.employees.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<EmployeeListResponse> | EmployeeListResponse>(url)
    // Handle both wrapped and direct responses
    if ('data' in response.data && response.data.data && 'success' in response.data) {
      return (response.data as ApiResponse<EmployeeListResponse>).data
    }
    // API returns direct response with structure: { totalRecords, pageNumber, pageSize, data }
    return response.data as EmployeeListResponse
  }

  /**
   * Get single employee by ID
   */
  async getEmployeeById(branchId: number, id: number): Promise<Employee> {
    const url = endpoints.employees.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<Employee> | Employee>(url)
    // Handle both wrapped and direct responses
    if ('data' in response.data && response.data.data) {
      return (response.data as ApiResponse<Employee>).data
    }
    return response.data as Employee
  }

  /**
   * Create new employee (multipart/form-data)
   */
  async saveEmployee(formData: FormData): Promise<ApiResponse<Employee>> {
    const response = await apiClient.post<ApiResponse<Employee>>(
      endpoints.employees.save,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  /**
   * Update existing employee (multipart/form-data)
   */
  async updateEmployee(formData: FormData): Promise<ApiResponse<Employee>> {
    const response = await apiClient.put<ApiResponse<Employee>>(
      endpoints.employees.update,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  /**
   * Get gender lookups
   */
  async getGenderLookups(): Promise<GenderLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.gender)
      console.log('Gender lookup response:', response.data)
      
      // Handle different response structures
      let data = response.data
      
      // If wrapped in ApiResponse
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      // If direct array
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: GenderLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching gender lookups:', error)
      return []
    }
  }

  /**
   * Get department lookups
   */
  async getDepartmentLookups(): Promise<DepartmentLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.departments)
      console.log('Department lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: DepartmentLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching department lookups:', error)
      return []
    }
  }

  /**
   * Get country lookups
   */
  async getCountryLookups(): Promise<CountryLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.countries)
      console.log('Country lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: CountryLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching country lookups:', error)
      return []
    }
  }

  /**
   * Get city lookups
   */
  async getCityLookups(): Promise<CityLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.cities)
      console.log('City lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: CityLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching city lookups:', error)
      return []
    }
  }

  /**
   * Get profession in iqama lookups
   */
  async getProfessionIqamaLookups(): Promise<ProfessionIqamaLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.professionIqama)
      console.log('Profession iqama lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: ProfessionIqamaLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching profession iqama lookups:', error)
      return []
    }
  }

  /**
   * Get notice period lookups
   */
  async getNoticePeriodLookups(): Promise<NoticePeriodLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.noticePeriod)
      console.log('Notice period lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: NoticePeriodLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching notice period lookups:', error)
      return []
    }
  }

  /**
   * Get marital status lookups
   */
  async getMaritalStatusLookups(): Promise<MaritalStatusLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.maritalStatus)
      console.log('Marital status lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: MaritalStatusLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching marital status lookups:', error)
      return []
    }
  }

  /**
   * Get job lookups
   */
  async getJobLookups(): Promise<JobLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.jobs)
      console.log('Job lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: JobLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching job lookups:', error)
      return []
    }
  }
}

export const employeesService = new EmployeesService()
